import { useEffect, useRef, useCallback } from 'react'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/contexts/AuthContext'
import { useTenant } from '@/hooks/useTenant'
import { toast } from '@/hooks/use-toast'
import { withAuthRetry, extractErrorMessage } from '@/lib/authUtils'
import { log } from '@/lib/logger'
import { getOrganizationTree } from '@/lib/organizationHelpers'
import { autoMergeIntoExisting, checkForDuplicateCandidate, createCandidate, DuplicateCheckResult } from '@/lib/candidateHelpers'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { mapLimit } from '@/lib/parallel'

export interface ContactPhone {
  type: 'work' | 'mobile' | 'other'
  number: string
  raw_number?: string | null
}

export interface ContactEmail {
  type: 'work' | 'personal' | 'other'
  email: string
  status?: string | null
}

export interface IndependentCandidate {
  id: string
  candidate_name: string
  email: string | null
  phone: string | null
  contact_phones?: ContactPhone[] | any[] | null
  contact_emails?: ContactEmail[] | any[] | null
  location_country: string | null
  location_state: string | null
  location_city: string | null
  salary_amount: number | null
  salary_currency: string | null
  salary_period: string | null
  profile_summary: string | null
  linkedin_url: string | null
  resume_url: string | null
  skills: string[] | null
  standardized_skills: string[] | null
  auto_generated_skills: any[] | null
  status: string
  source: string
  created_at: string
  updated_at: string
  created_by: string | null
  organization_id: string | null
  seniority_level: string | null
  functional_area: string | null
  specialization: string | null
  standardized_title: string | null
  years_experience: number | null
  enrichment_status: string | null
  current_job_title: string | null
  company_current: string | null
  /** Find Phase 3: loaded on the profile only. */
  apollo_id?: string | null
  field_provenance?: unknown
  profile_refreshed_at?: string | null
}

export interface CreateIndependentCandidateData {
  candidate_name: string
  email?: string | null
  phone?: string | null
  contact_emails?: ContactEmail[] | null
  contact_phones?: ContactPhone[] | null
  location_country?: string | null
  location_state?: string | null
  location_city?: string | null
  salary_amount?: number | null
  salary_currency?: string | null
  salary_period?: string | null
  profile_summary?: string | null
  linkedin_url?: string | null
  resume_url?: string | null
  skills?: string[] | null
  status?: string
  source?: string
  current_job_title?: string | null
  company_current?: string | null
  /** Aliases used by the candidate form / resume parser. */
  current_role?: string | null
  current_company?: string | null
  organization_id?: string | null
}


export interface DuplicateResult {
  isDuplicate: true
  existingCandidate: IndependentCandidate
  incomingData: CreateIndependentCandidateData
  mergedData: any
}

// Org tree cache (module-level, reset via effect when org changes)
const orgTreeCache = new Map<string, string[]>()

async function fetchIndependentCandidates(organizationId: string): Promise<IndependentCandidate[]> {
  // Resolve org tree (cached)
  let orgIds = orgTreeCache.get(organizationId)
  if (!orgIds) {
    orgIds = await getOrganizationTree(organizationId)
    orgTreeCache.set(organizationId, orgIds)
  }

  // The API returns at most 1,000 rows per request. The first page also brings
  // the total, so the remaining pages load side by side instead of one after
  // another (2026-10-17). Newest first, with id as a tiebreaker so pages never
  // overlap or skip rows.
  const PAGE = 1000
  const COLUMNS = 'id,candidate_name,email,phone,contact_phones,contact_emails,location_country,location_state,location_city,salary_amount,salary_currency,salary_period,profile_summary,linkedin_url,resume_url,skills,standardized_skills,auto_generated_skills,status,source,created_at,updated_at,created_by,organization_id,seniority_level,functional_area,specialization,standardized_title,years_experience,enrichment_status,current_job_title,company_current'
  const page = (from: number, withCount: boolean) =>
    withAuthRetry(async () =>
      await supabase
        .from('candidates')
        .select(COLUMNS, withCount ? { count: 'exact' } : undefined)
        .in('organization_id', orgIds!)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, from + PAGE - 1)
    )

  const first = await page(0, true)
  if (first.error) throw first.error
  const data: any[] = [...(first.data || [])]
  const total = first.count ?? data.length
  if (data.length === PAGE && total > PAGE) {
    const starts: number[] = []
    for (let from = PAGE; from < total; from += PAGE) starts.push(from)
    const rest = await mapLimit(starts, 4, async (from) => {
      const { data: rows, error } = await page(from, false)
      if (error) throw error
      return rows || []
    })
    for (const rows of rest) data.push(...rows)
    // Rows added while the pages loaded can push older ones past the count;
    // keep going one page at a time until a short page, as before.
    let last = rest[rest.length - 1]
    let from = starts[starts.length - 1] + PAGE
    while (last && last.length === PAGE) {
      const { data: rows, error } = await page(from, false)
      if (error) throw error
      last = rows || []
      data.push(...last)
      from += PAGE
    }
    // A row added mid-load shifts the pages by one, so a row can appear twice.
    const seen = new Set<string>()
    for (let i = data.length - 1; i >= 0; i--) {
      if (seen.has(data[i].id)) data.splice(i, 1)
      else seen.add(data[i].id)
    }
  }

  return data.map(candidate => ({
    ...candidate,
    auto_generated_skills: (candidate.auto_generated_skills as any) || null
  })) as IndependentCandidate[]
}

export function useIndependentCandidates() {
  const { user, organizationId, userType } = useAuth()
  const { tenant } = useTenant()
  const queryClient = useQueryClient()
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const queryKey = ['independent-candidates', organizationId] as const

  // Clear org tree cache when org changes
  useEffect(() => {
    if (organizationId) orgTreeCache.delete(organizationId)
  }, [organizationId])

  const { data: candidates = [], isLoading, error: queryError } = useQuery({
    queryKey,
    queryFn: () => fetchIndependentCandidates(organizationId!),
    enabled: !!user && !!organizationId,
    staleTime: 30_000, // 30 seconds
    refetchOnWindowFocus: false,
  })

  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to fetch candidates') : null

  // Backward-compat refresh function
  const getCandidates = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['independent-candidates'] })
  }, [queryClient])

  const addCandidate = async (
    candidateData: CreateIndependentCandidateData,
    options?: { skipRefresh?: boolean; silent?: boolean }
  ): Promise<IndependentCandidate | DuplicateResult | null> => {
    if (!user || !organizationId) throw new Error('User not authenticated or no organization context')

    try {
      const duplicateCheck = await checkForDuplicateCandidate(candidateData, organizationId, tenant?.id)

      // Already in Gio and nothing contradicts it: fill their record instead
      // of creating a second one (Find Phase 1, 2026-10-12).
      if (duplicateCheck?.autoMergeable) {
        const id = await autoMergeIntoExisting(duplicateCheck.existingCandidate.id, candidateData)
        if (!options?.silent) {
          toast({ title: 'Already in Gio', description: `${duplicateCheck.existingCandidate.candidate_name} was already a candidate (${duplicateCheck.reasons.join(', ').toLowerCase()}). Their profile was updated.` })
        }
        if (!options?.skipRefresh) queryClient.invalidateQueries({ queryKey: ['independent-candidates'] })
        const { data: merged } = await supabase.from('candidates').select('*').eq('id', id).single()
        return merged as IndependentCandidate
      }

      if (duplicateCheck) {
        return {
          isDuplicate: true,
          existingCandidate: duplicateCheck.existingCandidate as IndependentCandidate,
          incomingData: candidateData,
          mergedData: duplicateCheck.mergedData
        }
      }

      const newCandidate = await createCandidate({
        ...candidateData,
        candidate_name: candidateData.candidate_name,
        organization_id: organizationId,
        created_by: user.id,
        status: candidateData.status || 'active',
        source: candidateData.source || 'direct'
      })

      // Handle constraint-based duplicate (23505 caught inside createCandidate)
      if (newCandidate && (newCandidate as any).isDuplicate) {
        return {
          isDuplicate: true,
          existingCandidate: (newCandidate as any).existingCandidate as IndependentCandidate,
          incomingData: candidateData,
          mergedData: (newCandidate as any).mergedData
        }
      }

      if (!options?.silent) {
        toast({ title: 'Success', description: 'Candidate added successfully' })
      }

      if (!options?.skipRefresh) {
        queryClient.invalidateQueries({ queryKey: ['independent-candidates'] })
      }

      return {
        ...(newCandidate as any),
        auto_generated_skills: ((newCandidate as any).auto_generated_skills) || null
      } as IndependentCandidate
    } catch (err) {
      const errorMessage = extractErrorMessage(err)
      log.error('Independent candidate creation error:', err)
      if (!options?.silent) {
        toast({ title: 'Error', description: errorMessage, variant: 'destructive' })
      }
      throw err
    }
  }

  const updateCandidate = async (id: string, candidateData: Partial<CreateIndependentCandidateData>) => {
    try {
      const updateData: Record<string, any> = {}
      if (candidateData.candidate_name !== undefined) updateData.candidate_name = candidateData.candidate_name
      if (candidateData.email !== undefined) updateData.email = candidateData.email
      if (candidateData.phone !== undefined) updateData.phone = candidateData.phone
      if (candidateData.contact_emails !== undefined) updateData.contact_emails = candidateData.contact_emails?.map(e => JSON.stringify(e)) || []
      if (candidateData.contact_phones !== undefined) updateData.contact_phones = candidateData.contact_phones?.map(p => JSON.stringify(p)) || []
      if (candidateData.location_country !== undefined) updateData.location_country = candidateData.location_country
      if (candidateData.location_state !== undefined) updateData.location_state = candidateData.location_state
      if (candidateData.location_city !== undefined) updateData.location_city = candidateData.location_city
      if (candidateData.salary_amount !== undefined) updateData.salary_amount = candidateData.salary_amount
      if (candidateData.salary_currency !== undefined) updateData.salary_currency = candidateData.salary_currency
      if (candidateData.salary_period !== undefined) updateData.salary_period = candidateData.salary_period
      if (candidateData.profile_summary !== undefined) updateData.profile_summary = candidateData.profile_summary
      if (candidateData.linkedin_url !== undefined) updateData.linkedin_url = candidateData.linkedin_url
      if (candidateData.resume_url !== undefined) updateData.resume_url = candidateData.resume_url
      if (candidateData.skills !== undefined) updateData.skills = candidateData.skills
      if (candidateData.status !== undefined) updateData.status = candidateData.status
      if (candidateData.source !== undefined) updateData.source = candidateData.source
      const nextTitle = candidateData.current_job_title ?? candidateData.current_role
      if (nextTitle !== undefined) updateData.current_job_title = nextTitle || null
      const nextCompany = candidateData.company_current ?? candidateData.current_company
      if (nextCompany !== undefined) updateData.company_current = nextCompany || null


      const { data: updatedCandidate, error: updateError } = await withAuthRetry(async () =>
        await supabase
          .from('candidates')
          .update(updateData)
          .eq('id', id)
          .select()
          .single()
      )

      if (updateError) throw updateError

      toast({ title: 'Success', description: 'Candidate updated successfully' })
      queryClient.invalidateQueries({ queryKey: ['independent-candidates'] })
      return updatedCandidate
    } catch (err) {
      const errorMessage = extractErrorMessage(err)
      log.error('Independent candidate update error:', err)
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' })
      throw err
    }
  }

  /** `quiet`: the caller shows success and errors itself (the delete dialog does). */
  const deleteCandidate = async (id: string, opts: { quiet?: boolean } = {}) => {
    try {
      // Upcoming interviews are cancelled properly first (emails, calendar
      // events), as the delete dialog promises; the delete itself only removes
      // the rows (2026-10-13). If one can't be cancelled, nothing is deleted.
      const { data: upcoming } = await supabase
        .from('scheduled_bookings')
        .select('id')
        .eq('candidate_id', id)
        .in('status', ['confirmed', 'rescheduled'])
        .gte('scheduled_start', new Date().toISOString())
      let failed = 0
      for (const b of (upcoming ?? []) as { id: string }[]) {
        const { error } = await supabase.functions.invoke('cancel-booking', {
          body: { booking_id: b.id, reason: 'Candidate removed from Gio' },
        })
        if (error) failed++
      }
      if (failed) {
        throw new Error(`Couldn't cancel ${failed} upcoming interview${failed === 1 ? '' : 's'}, so the candidate wasn't deleted. Try again.`)
      }

      if (userType === 'platform_admin') {
        const { data, error: edgeFunctionError } = await supabase.functions.invoke('admin-operations', {
          body: { action: 'delete-candidate', candidate_id: id }
        })

        if (edgeFunctionError) throw edgeFunctionError
        if (!data?.success) throw new Error(data?.error || 'Failed to delete candidate')

        if (!opts.quiet) toast({ title: 'Candidate deleted' })
      } else {
        const { error: deleteError } = await withAuthRetry(async () =>
          await supabase.from('candidates').delete().eq('id', id)
        )
        if (deleteError) throw deleteError
        if (!opts.quiet) toast({ title: 'Candidate deleted' })
      }

      queryClient.invalidateQueries({ queryKey: ['independent-candidates'] })
    } catch (err) {
      const errorMessage = extractErrorMessage(err)
      log.error('Independent candidate deletion error:', err)
      if (!opts.quiet) toast({ title: 'Error', description: errorMessage, variant: 'destructive' })
      throw err
    }
  }

  // Debounced invalidation for real-time
  const debouncedInvalidate = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['independent-candidates'] })
    }, 2000)
  }, [queryClient])

  // Real-time subscription
  useEffect(() => {
    if (!user?.id || !organizationId) return

    let channel: any

    const setupSubscription = async () => {
      let orgIds = orgTreeCache.get(organizationId)
      if (!orgIds) {
        orgIds = await getOrganizationTree(organizationId)
        orgTreeCache.set(organizationId, orgIds)
      }

      const channelId = Math.random().toString(36).substr(2, 9)
      channel = supabase.channel(`candidates-changes-${channelId}`)

      orgIds.forEach(orgId => {
        channel.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'candidates',
            filter: `organization_id=eq.${orgId}`
          },
          () => {
            if ((window as any).__enrichmentActive) return
            debouncedInvalidate()
          }
        )
      })

      channel.subscribe()
    }

    setupSubscription()

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (channel) supabase.removeChannel(channel)
    }
  }, [user?.id, organizationId, debouncedInvalidate])

  return {
    candidates,
    isLoading,
    error,
    getCandidates,
    addCandidate,
    updateCandidate,
    deleteCandidate
  }
}
